import { useState } from "react";
import { Upload, X, Check, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";

const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const SAMPLE = `name,type,day_of_week,start_time,duration_min,capacity,instructor,mat
Fundamentals,gi,0,18:30,60,30,Coach Smith,Mat A
Advanced No-Gi,no-gi,2,19:30,75,20,Coach Smith,Mat A
Peewee Kids,peewee,3,17:00,45,15,Coach Ana,Mat B
Open Mat,open,5,10:00,90,40,,Mat A`;

// Accept either "0"..."6" or day names ("Mon", "monday", "Segunda", etc.)
const DAY_NAME_MAP = {
  mon: 0, monday: 0, seg: 0, segunda: 0, lun: 0, lunes: 0,
  tue: 1, tues: 1, tuesday: 1, ter: 1, terca: 1, "terça": 1, mar: 1, martes: 1,
  wed: 2, weds: 2, wednesday: 2, qua: 2, quarta: 2, mie: 2, "miércoles": 2, miercoles: 2,
  thu: 3, thur: 3, thurs: 3, thursday: 3, qui: 3, quinta: 3, jue: 3, jueves: 3,
  fri: 4, friday: 4, sex: 4, sexta: 4, vie: 4, viernes: 4,
  sat: 5, saturday: 5, sab: 5, sabado: 5, "sábado": 5,
  sun: 6, sunday: 6, dom: 6, domingo: 6,
};

function coerceDay(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s === "") return NaN;
  if (/^[0-6]$/.test(s)) return Number(s);
  return DAY_NAME_MAP[s] ?? NaN;
}

function splitLine(line) {
  const out = []; let cur = ""; let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === "," && !inQ) {
      out.push(cur); cur = "";
    } else { cur += ch; }
  }
  out.push(cur);
  return out.map(s => s.trim());
}

function parseCSV(text) {
  const lines = text.replace(/\r/g, "").split("\n").filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = splitLine(lines[0]).map(h => h.toLowerCase());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitLine(lines[i]);
    const row = {};
    headers.forEach((h, j) => { row[h] = cells[j] ?? ""; });
    if (!row.name) continue;
    // Coerce numerics + day
    row.day_of_week = coerceDay(row.day_of_week);
    if (row.duration_min !== undefined && row.duration_min !== "") row.duration_min = Number(row.duration_min) || 60;
    if (row.capacity !== undefined && row.capacity !== "") row.capacity = Number(row.capacity) || 20;
    rows.push(row);
  }
  return rows;
}

function ClassPreviewRow({ r }) {
  const invalid = !Number.isInteger(r.day_of_week) || r.day_of_week < 0 || r.day_of_week > 6;
  return (
    <tr style={{borderBottom:"1px solid var(--border)", opacity: invalid ? 0.5 : 1}}>
      <td className="p-3">{r.name}</td>
      <td className="p-3 label-tiny capitalize">{r.type || "gi"}</td>
      <td className="p-3 label-tiny">{invalid ? <span style={{color:"var(--danger)"}}>?</span> : DAYS_SHORT[r.day_of_week]}</td>
      <td className="p-3 mono label-tiny">{r.start_time}</td>
      <td className="p-3 text-right mono">{r.capacity || 20}</td>
      <td className="p-3 label-tiny">{r.instructor || "—"}</td>
    </tr>
  );
}

function ImportPreviewTable({ rows, invalidCount }) {
  return (
    <div className="mt-5">
      <div className="label">Preview · {rows.length} rows{invalidCount > 0 && <span style={{color:"var(--warn)"}}> · {invalidCount} invalid</span>}</div>
      <div className="card mt-2 overflow-auto" style={{maxHeight: 260}}>
        <table className="w-full text-sm">
          <thead><tr style={{borderBottom:"1px solid var(--border)"}}>
            <th className="label-tiny text-left p-3">Name</th>
            <th className="label-tiny text-left p-3">Type</th>
            <th className="label-tiny text-left p-3">Day</th>
            <th className="label-tiny text-left p-3">Time</th>
            <th className="label-tiny text-right p-3">Cap</th>
            <th className="label-tiny text-left p-3">Instructor</th>
          </tr></thead>
          <tbody>
            {rows.slice(0, 15).map((r, i) => <ClassPreviewRow key={`${r.name}-${r.day_of_week}-${r.start_time}-${i}`} r={r} />)}
          </tbody>
        </table>
        {rows.length > 15 && <div className="label-tiny p-3 text-center">…and {rows.length - 15} more</div>}
      </div>
    </div>
  );
}

function ImportResultView({ result, onDone }) {
  return (
    <div className="text-center py-4" data-testid="import-classes-result">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4" style={{background:"var(--green-bg)", border:"1px solid var(--green-border)"}}>
        <Check size={22} style={{color:"var(--green)"}}/>
      </div>
      <div className="text-[20px] font-bold uppercase tracking-tight">Import complete</div>
      <div className="grid grid-cols-3 gap-3 mt-5">
        <div className="card p-4"><div className="label-tiny">Added</div><div className="text-[24px] font-bold mt-1" style={{color:"var(--green)"}}>{result.added}</div></div>
        <div className="card p-4"><div className="label-tiny">Skipped</div><div className="text-[24px] font-bold mt-1" style={{color:"var(--text-dim)"}}>{result.skipped}</div></div>
        <div className="card p-4"><div className="label-tiny">Invalid</div><div className="text-[24px] font-bold mt-1" style={{color:"var(--warn)"}}>{result.invalid || 0}</div></div>
      </div>
      {(result.errors || []).length > 0 && <div className="mt-4 label-tiny text-left" style={{color:"var(--warn)"}}>{result.errors.length} warnings — first few: {result.errors.slice(0,3).join("; ")}</div>}
      <button onClick={onDone} className="btn btn-primary w-full mt-6" data-testid="import-classes-done">Done</button>
    </div>
  );
}

export default function ImportClassesModal({ onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onFile = async (e) => {
    setError("");
    const f = e.target.files?.[0]; if (!f) return;
    const text = await f.text();
    const parsed = parseCSV(text);
    if (parsed.length === 0) { setError("No rows detected. Check the header row."); return; }
    setRows(parsed);
  };

  const useSample = () => { setRows(parseCSV(SAMPLE)); setError(""); };

  const submit = async () => {
    setBusy(true); setError("");
    try {
      // Filter out rows with invalid day
      const valid = rows.filter(r => Number.isInteger(r.day_of_week) && r.day_of_week >= 0 && r.day_of_week <= 6);
      const invalid = rows.length - valid.length;
      if (valid.length === 0) {
        setError(`No valid rows to import. ${invalid} rows have invalid day_of_week.`);
        setBusy(false); return;
      }
      const { data } = await api.post("/classes/import", { rows: valid });
      setResult({ ...data, invalid });
    } catch (e) { setError(formatApiError(e, "Import failed")); }
    setBusy(false);
  };

  const invalidCount = rows.filter(r => !Number.isInteger(r.day_of_week) || r.day_of_week < 0 || r.day_of_week > 6).length;

  return (
    <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", display:"grid", placeItems:"center", zIndex:60, padding:16}} onClick={onClose}>
      <div className="card p-7 w-[720px] max-w-[92vw] max-h-[92vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="import-classes-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label" style={{color:"var(--green)"}}>Schedule import</div>
            <div className="text-[20px] font-bold uppercase mt-1 tracking-tight">Import classes from CSV</div>
          </div>
          <button onClick={onClose} data-testid="import-classes-close"><X size={18}/></button>
        </div>

        {!result ? (
          <>
            <div className="label-tiny mb-3 leading-relaxed">
              CSV columns: <span className="mono" style={{color:"var(--green)"}}>name, type, day_of_week, start_time, duration_min, capacity, instructor, mat</span>.<br/>
              <span style={{color:"var(--text-dim)"}}>
                <span className="mono">day_of_week</span> accepts 0–6 (0=Mon) OR names like "Mon", "monday", "segunda", "lunes".
                <span className="mono"> start_time</span> is 24h "HH:MM" (e.g. 18:30).
              </span>
              {" "}<button onClick={useSample} className="underline" style={{color:"var(--text-dim)"}} data-testid="use-sample-classes">Use sample</button>
            </div>
            <label className="card card-hover p-6 flex flex-col items-center gap-2 cursor-pointer" data-testid="classes-file-drop">
              <Upload size={22} style={{color:"var(--green)"}}/>
              <div className="font-semibold text-[14px]">Choose CSV file</div>
              <div className="label-tiny">UTF-8 · header row required</div>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="import-classes-file"/>
            </label>

            {rows.length > 0 && <ImportPreviewTable rows={rows} invalidCount={invalidCount} />}

            {error && <div className="label-tiny mt-3 flex items-center gap-2" style={{color:"var(--danger)"}} data-testid="import-classes-error"><AlertCircle size={14}/>{error}</div>}
            <button onClick={submit} disabled={busy || rows.length===0} className="btn btn-primary w-full mt-5" data-testid="import-classes-submit">
              {busy ? "Importing…" : `Import ${rows.length - invalidCount} classes`}
            </button>
          </>
        ) : (
          <ImportResultView result={result} onDone={() => { onDone?.(); onClose(); }} />
        )}
      </div>
    </div>
  );
}
